import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as z from 'zod';

import { isValidPhoneNumber } from 'reservations/base/helpers/phone';
import {
  useDefaultBaseFormValues,
  useDefaultBookingFormValues
} from 'reservations/form/hooks/useFormValues';
import { useBookingForm } from 'reservations/form/providers/BookingFormProvider';
import { defaultWhenNullish } from 'utility/zod';

const baseFormSchema = z.object({
  date: z.date(),
  partySize: z.number(),
  name: z.string().min(1, { message: 'Name is required' }),
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Enter a valid email address' }),
  specialOccasion: z.string(),
  note: z
    .string()
    .max(500, { message: 'Notes cannot be longer than 500 characters' }),
  phone: z.string().min(1),
  phoneCode: z.string().min(1),
  fullPhoneNumber: z
    .string()
    .min(1, { message: 'Phone number is required' })
    .refine(isValidPhoneNumber, {
      message: 'Enter a valid phone number'
    }),
  optInMarketingEmail: z.boolean(),
  optInMarketingSms: z.boolean(),
  savedPaymentMethod: defaultWhenNullish(z.string(), ''),
  optInChecklessPayments: z.boolean(),
  tipPercentage: defaultWhenNullish(z.number().int().nonnegative().lte(100), 0)
});
export type BaseFormSchema = z.infer<typeof baseFormSchema>;

const bookingFormSchema = baseFormSchema.extend({
  time: z.number().nullish(),
  availability: z.string().nullish()
});
export type BookingFormSchema = z.infer<typeof bookingFormSchema>;

type FormProps = {
  defaultFormValues?: BookingFormSchema | BaseFormSchema;
  children?: ReactNode;
};

function useReservationsForm(
  defaultFormValues?: BookingFormSchema | BaseFormSchema
) {
  const { isWaitlist } = useBookingForm();
  const formSchema = isWaitlist ? baseFormSchema : bookingFormSchema;
  const formValuesHook = isWaitlist
    ? useDefaultBaseFormValues
    : useDefaultBookingFormValues;
  const defaultValues = formValuesHook(defaultFormValues);

  return useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues
  });
}

const Form = ({ children, defaultFormValues }: FormProps) => {
  const methods = useReservationsForm(defaultFormValues);
  const { submissionType } = useBookingForm();

  return (
    <FormProvider data-test-id="for\m-provider" {...methods}>
      <form data-testid={`form-${submissionType}`}>{children}</form>
    </FormProvider>
  );
};

export default Form;
